import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Typography,
  Paper,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  Layers as ScenariosIcon,
  Storage as DatasetsIcon,
  Assessment as StatsIcon,
} from '@mui/icons-material';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';
import { ScenarioDatasetBreadcrumbs } from '../components/navigation/ScenarioDatasetBreadcrumbs';
import { ScenarioList } from '../components/scenarios/ScenarioList';
import { DatasetList } from '../components/datasets/DatasetList';
import { DatasetStatistics } from '../components/datasets/DatasetStatistics';
import { useVRPData } from '../contexts/VRPDataContext';
import { TabsErrorBoundary } from '../components/error/TabsErrorBoundary';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scenario-tabpanel-${index}`}
      aria-labelledby={`scenario-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={value === index} timeout={300}>
          <Box sx={{ pt: 3 }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `scenario-tab-${index}`,
    'aria-controls': `scenario-tabpanel-${index}`,
  };
}

export const ScenarioManagement: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const { datasets, currentDataset } = useVRPData();
  
  const [tabValue, setTabValue] = useState(0);
  const [createScenarioOpen, setCreateScenarioOpen] = useState(false);
  const [createDatasetOpen, setCreateDatasetOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Defensive tab value validation to prevent React error #185
  const safeTabValue = React.useMemo(() => {
    if (tabValue === null || tabValue === undefined) {
      console.warn('🔧 [ScenarioManagement] tabValue is null/undefined, defaulting to 0');
      return 0;
    }
    
    const numericValue = Number(tabValue);
    if (isNaN(numericValue) || numericValue < 0 || numericValue > 2 || !Number.isInteger(numericValue)) {
      console.warn('🔧 [ScenarioManagement] Invalid tabValue detected, resetting to 0:', tabValue, 'parsed:', numericValue);
      return 0;
    }
    return numericValue;
  }, [tabValue]);

  const handleTabChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
    // Defensive validation to prevent React error #185
    if (!event || event === null) {
      console.warn('⚠️ [ScenarioManagement] Invalid event in handleTabChange:', event);
      return;
    }
    
    // Ensure newValue is a valid number and within range
    const validatedValue = Number(newValue);
    if (isNaN(validatedValue) || validatedValue < 0 || validatedValue > 2) {
      console.warn('⚠️ [ScenarioManagement] Invalid tab value, keeping current:', newValue, 'current:', tabValue);
      return;
    }
    
    console.log('🔄 [ScenarioManagement] Tab change:', { from: tabValue, to: validatedValue });
    
    // Only update if the value actually changed to prevent unnecessary re-renders
    if (validatedValue !== tabValue) {
      setTabValue(validatedValue);
    }
  }, [tabValue]);

  const handleCreateScenario = () => {
    setCreateScenarioOpen(true);
  };

  const handleCreateDataset = () => {
    setCreateDatasetOpen(true);
  };

  // Enhanced URL-based project validation with improved coordination
  useEffect(() => {
    const debugInfo = {
      projectId,
      projectIdType: typeof projectId,
      currentProject: currentProject ? { id: currentProject.id, name: currentProject.name } : null,
      projectsCount: projects.length,
      isLoading: isLoading,
      isInitializing
    };
    
    console.log('🔄 [ScenarioManagement] Project validation check:', debugInfo);

    // Enhanced timeout with progressive checks
    const loadingTimeout = setTimeout(() => {
      if (isInitializing) {
        console.warn('⚠️ [ScenarioManagement] Loading timeout reached (10s):', {
          stillLoading: isLoading,
          hasProjectId: !!projectId,
          hasProjects: projects.length > 0,
          hasCurrentProject: !!currentProject
        });
        
        // Force completion if we're stuck
        setIsInitializing(false);
      }
    }, 10000);

    // Main validation logic with enhanced error handling
    if (projectId) {
      const numericProjectId = parseInt(projectId, 10);
      
      if (isNaN(numericProjectId) || numericProjectId <= 0) {
        console.error('❌ [ScenarioManagement] Invalid project ID format:', {
          original: projectId,
          parsed: numericProjectId
        });
        setIsInitializing(false);
        return () => clearTimeout(loadingTimeout);
      }

      // If navigation is not loading and we have projects data
      if (!isLoading.navigation && projects.length > 0) {
        if (currentProject?.id === numericProjectId) {
          console.log('✅ [ScenarioManagement] Project context successfully resolved:', {
            projectId: currentProject.id,
            projectName: currentProject.name,
            loadTime: 'immediate'
          });
          setIsInitializing(false);
        } else {
          // Check if project exists in the loaded projects
          const targetProject = projects.find(p => p.id === numericProjectId);
          if (targetProject) {
            console.warn('⚠️ [ScenarioManagement] Project exists but context not yet resolved:', {
              targetProject: { id: targetProject.id, name: targetProject.name },
              currentProject: currentProject?.id || null,
              willRetryContextResolution: true
            });
            
            // Give the context more time to resolve (reduced from 500ms to 200ms)
            setTimeout(() => {
              if (isInitializing) {
                console.log('⏰ [ScenarioManagement] Context resolution timeout, completing initialization');
                setIsInitializing(false);
              }
            }, 200);
          } else {
            console.error('❌ [ScenarioManagement] Project not found in available projects:', {
              requestedId: numericProjectId,
              availableProjects: projects.map(p => ({ id: p.id, name: p.name }))
            });
            setIsInitializing(false);
          }
        }
      } else if (!isLoading.navigation && projects.length === 0) {
        console.warn('⚠️ [ScenarioManagement] No projects loaded but navigation complete:', {
          possibleIssues: ['API error', 'No projects exist', 'Authentication issue']
        });
        setIsInitializing(false);
      }
      // If navigation is still loading, wait for it to complete
    } else {
      console.log('ℹ️ [ScenarioManagement] No project ID in URL, completing initialization');
      setIsInitializing(false);
    }

    return () => clearTimeout(loadingTimeout);
  }, [projectId, projects, currentProject, isLoading.navigation, isInitializing]);

  // Show loading state during initialization
  if (isInitializing || isLoading.navigation) {
    return (
      <ProfessionalLayout
        pageTitle="Scenario & Dataset Management"
        pageSubtitle="Organize your VRP data with scenarios and datasets for efficient planning and optimization"
        activeTab="Scenarios"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading project data...
          </Typography>
        </Box>
      </ProfessionalLayout>
    );
  }

  // Show error state if project not found
  if (projectId && !currentProject) {
    return (
      <ProfessionalLayout
        pageTitle="Scenario & Dataset Management"
        pageSubtitle="Organize your VRP data with scenarios and datasets for efficient planning and optimization"
        activeTab="Scenarios"
      >
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Project Not Found
          </Typography>
          <Typography variant="body1" color="textSecondary">
            The project with ID "{projectId}" could not be found. Please check the URL or select a valid project.
          </Typography>
        </Paper>
      </ProfessionalLayout>
    );
  }

  // Show general error state if no project is available
  if (!currentProject) {
    return (
      <ProfessionalLayout
        pageTitle="Scenario & Dataset Management"
        pageSubtitle="Organize your VRP data with scenarios and datasets for efficient planning and optimization"
        activeTab="Scenarios"
      >
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="textSecondary">
            Please select a project to manage scenarios and datasets
          </Typography>
        </Paper>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout
      pageTitle="Scenario & Dataset Management"
      pageSubtitle="Organize your VRP data with scenarios and datasets for efficient planning and optimization"
      activeTab="Scenarios"
      actionButtons={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ScenarioDatasetBreadcrumbs
            onCreateScenario={handleCreateScenario}
            onCreateDataset={handleCreateDataset}
          />
        </Box>
      }
    >

      {/* Main Content Tabs */}
      <Paper elevation={2}>
        <TabsErrorBoundary 
          fallbackTab={0}
          onTabError={(error, errorInfo) => {
            console.error('[ScenarioManagement] Tab error caught:', { error, errorInfo });
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={safeTabValue} 
              onChange={handleTabChange} 
              aria-label="scenario management tabs"
              variant="fullWidth"
            >
              <Tab 
                icon={<ScenariosIcon />} 
                label={`Scenarios (${(scenarios || []).length})`} 
                value={0}
                {...a11yProps(0)} 
              />
              <Tab 
                icon={<DatasetsIcon />} 
                label={`Datasets (${(datasets || []).length})`} 
                value={1}
                {...a11yProps(1)}
                disabled={!currentScenario}
              />
              <Tab 
                icon={<StatsIcon />} 
                label="Statistics" 
                value={2}
                {...a11yProps(2)}
                disabled={!currentDataset}
              />
            </Tabs>
          </Box>
        </TabsErrorBoundary>

        {/* Scenarios Tab */}
        <TabPanel value={safeTabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <ScenarioList />
          </Box>
        </TabPanel>

        {/* Datasets Tab */}
        <TabPanel value={safeTabValue} index={1}>
          <Box sx={{ p: 3 }}>
            {currentScenario ? (
              <React.Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              }>
                <DatasetList />
              </React.Suspense>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <ScenariosIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Select a scenario first
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Datasets are organized within scenarios. Please select a scenario to view and manage datasets.
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Statistics Tab */}
        <TabPanel value={safeTabValue} index={2}>
          <Box sx={{ p: 3 }}>
            {currentDataset ? (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <DatasetStatistics showRefresh={true} />
                </Grid>
                
                {/* Additional statistics views can be added here */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3, height: 300 }}>
                    <Typography variant="h6" gutterBottom>
                      Optimization History
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Chart showing optimization performance over time will be displayed here.
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3, height: 300 }}>
                    <Typography variant="h6" gutterBottom>
                      Geographic Distribution
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Map showing the geographic distribution of locations will be displayed here.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <StatsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Select a dataset first
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Statistics are available for individual datasets. Please select a dataset to view detailed analytics.
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <ScenariosIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" color="primary.main">
              {(scenarios || []).length}
            </Typography>
            <Typography variant="h6">
              Scenarios
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {(scenarios || []).filter(s => s.status === 'active').length} active, {' '}
              {(scenarios || []).filter(s => s.status === 'template').length} templates, {' '}
              {(scenarios || []).filter(s => s.status === 'archived').length} archived
            </Typography>
          </Paper>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <DatasetsIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
            <Typography variant="h4" color="secondary.main">
              {(datasets || []).length}
            </Typography>
            <Typography variant="h6">
              Datasets
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {(datasets || []).filter(d => d.status === 'active').length} active, {' '}
              {(datasets || []).filter(d => d.status === 'processing').length} processing, {' '}
              {(datasets || []).filter(d => d.status === 'archived').length} archived
            </Typography>
          </Paper>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <StatsIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" color="success.main">
              {(datasets || []).reduce((sum, d) => sum + d.optimization_count, 0)}
            </Typography>
            <Typography variant="h6">
              Total Optimizations
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Across all datasets in {currentScenario?.name || 'all scenarios'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </ProfessionalLayout>
  );
};